/**
 * Skio Plan Picker V2 UI extension.
 * Loaded when ui-version="v2" or options.ui_version === "v2".
 */
import { html, unsafeHTML, unsafeCSS } from 'https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js'
import { SkioPlanPicker } from './skio-plan-picker-component.js'
import { skioV2CssText } from './skio-plan-picker-v2-styles.js'

const v2ToplineStyle =
  'display:flex;flex-direction:row;flex-wrap:nowrap;align-items:center;justify-content:flex-start;width:100%;gap:8px'
const v2PricesStyle =
  'display:flex;flex-direction:row;flex-wrap:nowrap;align-items:center;gap:4px;margin-left:auto;white-space:nowrap'

/** Normalizes benefits HTML strings from JSON script payloads. */
function readBenefitsHtml(value) {
  if (typeof value !== 'string') return ''
  return value.trim()
}

const v2OriginalConnectedCallback = SkioPlanPicker.prototype.connectedCallback

SkioPlanPicker.prototype.connectedCallback = async function () {
  if (this.isV2()) {
    this.applyV2Settings?.()
  }

  const targetFormId = this.getAttribute('data-target-form-id')
  if (targetFormId && (!this.options?.form_id || this.options.form_id === '' || String(this.options.form_id).includes('{{'))) {
    this.options = { ...(this.options || {}), form_id: targetFormId }
  }

  await v2OriginalConnectedCallback.call(this)

  if (this.isV2()) {
    this.ensureV2StylesInjected?.()
    this.loadV2BenefitsData?.()
    this.applyV2BenefitsForVariant?.(this.selectedVariant)
    this.requestUpdate()
  }
}

const v2OriginalUpdated = SkioPlanPicker.prototype.updated

SkioPlanPicker.prototype.updated = function (changed) {
  if (this.isV2()) {
    this.ensureV2StylesInjected?.()
  }

  const result = v2OriginalUpdated.call(this, changed)

  if (this.isV2()) {
    this.ensureV2StylesInjected?.()

    if (changed.has('selectedVariant')) {
      this.applyV2BenefitsForVariant?.(this.selectedVariant)
      this.requestUpdate()
    }
  }

  return result
}

const v2OriginalSelectSellingPlanGroup = SkioPlanPicker.prototype.selectSellingPlanGroup

SkioPlanPicker.prototype.selectSellingPlanGroup = function (group) {
  v2OriginalSelectSellingPlanGroup.call(this, group)

  if (this.isV2()) {
    this.requestUpdate()
  }
}

Object.assign(SkioPlanPicker.prototype, {
  isDesignMode() {
    return Boolean(this.debug || window.Shopify?.designMode)
  },

  v2StylesTemplate() {
    return html`<style>
      ${unsafeCSS(skioV2CssText)}
    </style>`
  },

  ensureV2StylesInjected() {
    if (!this.isV2()) return

    const root = this.renderRoot
    if (!root) return

    let style = root.querySelector('#skio-v2-injected-styles')
    if (!style) {
      style = document.createElement('style')
      style.id = 'skio-v2-injected-styles'
    }

    style.textContent = skioV2CssText

    // Keep V2 stylesheet last in the shadow root so it wins over Lit's adoptedStyleSheets.
    root.appendChild(style)
  },

  applyV2Settings() {
    if (!this.isV2()) return

    this.applyTargetFormId?.()

    const v2SettingsRaw = this.getAttribute('data-v2-settings')
    if (v2SettingsRaw) {
      try {
        const v2Settings = JSON.parse(v2SettingsRaw)
        this.options = { ...(this.options || {}), ...v2Settings }
      } catch (e) {
        console.warn('[Skio V2] Could not parse data-v2-settings', e)
      }
    }

    const benefitsHtml = this.getAttribute('data-benefits-html')
    if (benefitsHtml) {
      this.options = { ...(this.options || {}), benefits_html: benefitsHtml }
    }

    this.loadV2BenefitsData()
    this.applyV2BenefitsForVariant(this.selectedVariant)

    if (this.options?.ui_version === 'v2') {
      this.setAttribute('ui-version', 'v2')
    }

    this.style.setProperty('--skio-v2-banner-bg', this.options?.banner_background || '#a2bfb9')
    this.style.setProperty('--skio-v2-banner-color', this.options?.banner_text_color || '#000')
    this.style.setProperty('--skio-v2-banner-border', this.options?.banner_border_color || '#000')
    this.style.setProperty('--skio-group-border-color', '#e5e5e5')
    this.style.setProperty('--skio-group-border-selected-color', '#000000')
  },

  getSavePercent(sellingPlan = this.selectedSellingPlan) {
    if (!sellingPlan) return 0
    const discount = this.discount(sellingPlan)
    const percent = discount?.percent
    if (!percent || percent === Infinity || percent < 0) return 0
    return Math.round(percent)
  },

  getSellingPlanDays(sellingPlan) {
    if (!sellingPlan) return null

    const policy = sellingPlan.delivery_policy || sellingPlan.billing_policy
    if (policy?.interval) {
      const interval = String(policy.interval).toUpperCase()
      const count = policy.interval_count || 1

      switch (interval) {
        case 'DAY':
          return count
        case 'WEEK':
          return count * 7
        case 'MONTH':
        case 'YEAR':
          return null
        default:
          break
      }
    }

    const match = sellingPlan.name?.match(/(\d+)\s*(day|week|month|year)s?/i)
    if (!match) return null

    const value = parseInt(match[1], 10)
    const unit = match[2].toLowerCase()

    if (unit === 'day') return value
    if (unit === 'week') return value * 7
    return null
  },

  /**
   * Days supply for price-per-day: variant ml at 1 ml/day (matches merchant spreadsheet).
   * Optional override via options.variant_days_supply[variantId] (e.g. from metafield).
   */
  getVariantDaysSupply() {
    const variant = this.selectedVariant
    if (!variant) return null

    const map = this.options?.variant_days_supply
    if (map && variant.id != null) {
      const fromMap = map[String(variant.id)] ?? map[variant.id]
      const days = parseInt(fromMap, 10)
      if (days > 0) return days
    }

    const sources = [variant.option1, variant.option2, variant.option3, variant.title, variant.public_title, variant.name]
    for (const src of sources) {
      if (!src) continue
      const match = String(src).match(/(\d+(?:[.,]\d+)?)\s*ml\b/i)
      if (!match) continue
      const ml = parseFloat(match[1].replace(',', '.'))
      if (ml > 0) return Math.round(ml)
    }

    return null
  },

  getPricePerDayText(sellingPlan = this.selectedSellingPlan) {
    if (!this.options?.show_price_per_day || !sellingPlan) return null

    const days = this.getVariantDaysSupply() ?? this.getSellingPlanDays(sellingPlan)
    if (!days || days <= 0) return null

    const priceCents = this.price(sellingPlan, false)
    const perDayCents = Math.round(priceCents / days)

    const prefix = this.options?.price_per_day_prefix || 'Clearer skin for just'
    const suffix = this.options?.price_per_day_suffix || 'a day'

    return `${prefix} ${this.money(perDayCents)} ${suffix}`
  },

  v2CheckIcon() {
    return html`<svg width="11" height="8" viewBox="0 0 11 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M4.44651e-05 4.23136L1.40203 2.8244L3.70388 5.08647L8.8097 0.000532746L10.2216 1.40749L3.70388 7.89045L4.44651e-05 4.23136Z" fill="#6B0010"/>
    </svg>`
  },

  loadV2BenefitsData() {
    if (this._v2BenefitsDataLoaded) return

    const readJsonScript = (el) => {
      if (!el?.textContent) return null
      try {
        return JSON.parse(el.textContent)
      } catch (error) {
        console.warn('[Skio V2] Could not parse benefits JSON', error)
        return null
      }
    }

    const byVariantScript =
      this.querySelector('[data-skio-v2-benefits-by-variant]') ||
      this.parentElement?.querySelector('[data-skio-v2-benefits-by-variant]')
    const byVariantData = readJsonScript(byVariantScript)

    if (byVariantData && typeof byVariantData === 'object') {
      this._v2BenefitsByVariant = {
        default: readBenefitsHtml(byVariantData.default),
        variants: byVariantData.variants && typeof byVariantData.variants === 'object' ? byVariantData.variants : {},
      }
      this._v2BenefitsDataLoaded = true
      return
    }

    const inlineScript = this.querySelector('[data-skio-v2-benefits]')
    const inlineParsed = readJsonScript(inlineScript)
    const inlineHtml = readBenefitsHtml(inlineParsed)

    if (inlineHtml) {
      this._v2BenefitsByVariant = { default: inlineHtml, variants: {} }
      this._v2BenefitsDataLoaded = true
      return
    }

    const template = this.querySelector('#skio-v2-benefits-template')
    if (template) {
      const templateHtml = (
        template.innerHTML ||
        template.content?.firstElementChild?.outerHTML ||
        Array.from(template.content?.childNodes || [])
          .map(node => node.textContent || node.outerHTML || '')
          .join('')
      ).trim()

      if (templateHtml) {
        this._v2BenefitsByVariant = { default: templateHtml, variants: {} }
        this._v2BenefitsDataLoaded = true
        return
      }
    }

    const benefitsId = this.getAttribute('data-benefits-id')
    if (benefitsId) {
      const jsonEl = document.getElementById(benefitsId)
      const externalParsed = readJsonScript(jsonEl)
      const externalHtml = readBenefitsHtml(externalParsed)
      if (externalHtml) {
        this._v2BenefitsByVariant = { default: externalHtml, variants: {} }
      }
    }

    this._v2BenefitsDataLoaded = true
  },

  getV2BenefitsHtmlForVariant(variant) {
    this.loadV2BenefitsData()

    const variantId = variant?.id != null ? String(variant.id) : ''
    const map = this._v2BenefitsByVariant

    if (map) {
      const variantHtml = readBenefitsHtml(map.variants?.[variantId])
      if (variantHtml) return variantHtml
      return readBenefitsHtml(map.default)
    }

    return ''
  },

  applyV2BenefitsForVariant(variant) {
    const benefitsHtml = this.getV2BenefitsHtmlForVariant(variant || this.selectedVariant)
    const previousHtml = this.options?.benefits_html

    this.options = { ...(this.options || {}), benefits_html: benefitsHtml }

    if (benefitsHtml !== previousHtml) {
      this.requestUpdate()
    }
  },

  loadV2BenefitsFromTemplate() {
    this.loadV2BenefitsData()
    this.applyV2BenefitsForVariant(this.selectedVariant)
  },

  getV2ComparePrice(plan) {
    const oneTimePrice = this.selectedVariant?.price
    if (!oneTimePrice || !plan) return null

    const subscriptionPrice = this.price(plan, false)
    if (subscriptionPrice >= oneTimePrice) return null

    return oneTimePrice
  },

  syncVariantFromEvent(variant) {
    if (!variant) return

    if (this.product?.variants?.length) {
      this.selectedVariant = this.product.variants.find(v => String(v.id) === String(variant.id)) || variant
    } else {
      this.selectedVariant = variant
    }

    this.variantChanged = true
    this.applyV2BenefitsForVariant?.(this.selectedVariant)
  },

  normalizeV2BenefitsHtml(html) {
    if (!html || typeof html !== 'string') return html

    try {
      const template = document.createElement('template')
      template.innerHTML = html.trim()

      template.content.querySelectorAll('li, p').forEach((line) => {
        if (line.querySelector(':scope > .skio-v2-benefit-text')) return

        const wrapper = document.createElement('span')
        wrapper.className = 'skio-v2-benefit-text'

        while (line.firstChild) {
          wrapper.appendChild(line.firstChild)
        }

        line.appendChild(wrapper)
      })

      return template.innerHTML
    } catch (error) {
      console.warn('[Skio V2] Could not normalize benefits HTML', error)
      return html
    }
  },

  renderV2Benefits() {
    this.applyV2BenefitsForVariant(this.selectedVariant)
    const benefitsHtml = this.options?.benefits_html
    if (!benefitsHtml || benefitsHtml.trim() === '') return null

    const normalizedHtml = this.normalizeV2BenefitsHtml(benefitsHtml)

    return html`
      <div class="skio-v2-benefits">${unsafeHTML(normalizedHtml)}</div>
    `
  },

  renderV2Gift(index) {
    const image = this.options?.[`gift_${index}_image`]
    const title = this.options?.[`gift_${index}_title`]
    const description = this.options?.[`gift_${index}_description`]
    const badge = this.options?.[`gift_${index}_badge`] || 'FREE'

    if (!title && !description && !image) return null

    return html`
      <div class="skio-v2-gift">
        ${image
          ? html`<img class="skio-v2-gift-image" src="${image}" alt="" width="47" height="47" loading="lazy">`
          : html`<span class="skio-v2-gift-image skio-v2-gift-image--placeholder" aria-hidden="true"></span>`}
        <div class="skio-v2-gift-body">
          ${title ? html`<div class="skio-v2-gift-title">${title}</div>` : ''}
          ${description ? html`<div class="skio-v2-gift-desc">${description}</div>` : ''}
        </div>
        <span class="skio-v2-gift-badge">${badge}</span>
      </div>
    `
  },

  renderV2Gifts() {
    const gifts = [this.renderV2Gift(1), this.renderV2Gift(2)].filter(Boolean)
    if (!gifts.length) return null

    return html`<div class="skio-v2-gifts">${gifts}</div>`
  },

  renderV2SubscriptionCard(group, index) {
    const isSelected = this.selectedSellingPlanGroup === group
    const plan = group.selected_selling_plan
    const savePercent = this.getSavePercent(plan)
    const compareAt = this.getV2ComparePrice(plan)

    const showBanner = this.options?.show_top_banner && index === 0

    const benefits = this.renderV2Benefits()
    const gifts = this.renderV2Gifts()
    let benefitsAndGiftsBlock = null
    if (benefits || gifts) {
      benefitsAndGiftsBlock = html`
        <div class="skio-v2-divider"></div>
        ${benefits}
        ${gifts}
      `
    }

    return html`
      <div class="skio-v2-subscription-wrap">
        ${showBanner
          ? html`<div class="skio-v2-banner">${this.options?.banner_text || 'MOST POPULAR & BEST VALUE'}</div>`
          : ''}

        <div
          class="group-container ${isSelected ? 'group-container--selected' : ''}"
          @click=${() => this.selectSellingPlanGroup(group)}
        >
          <input
            id="group-${index}-${this.key}"
            class="group-input"
            name="skio-group-${this.key}"
            type="radio"
            value="${group.id}"
            @change=${() => this.selectSellingPlanGroup(group)}
            ?checked=${isSelected}
          />

          <label class="group-label" for="group-${index}-${this.key}">
            <div class="skio-v2-topline" style="${v2ToplineStyle}">
              ${this.options?.show_radio_selector ? this.radioTemplate() : ''}
              <span class="skio-v2-title">
                ${group.name !== 'Prepaid' && this.options?.subscription_title
                  ? this.options.subscription_title
                  : group.name === 'Prepaid' && this.options?.prepaid_title
                  ? this.options.prepaid_title
                  : group.name}
              </span>
              <div class="skio-v2-prices" style="${v2PricesStyle}">
                ${compareAt ? html`<span class="skio-v2-compare">${this.money(compareAt)}</span>` : ''}
                <span class="skio-v2-sale">${this.price(plan)}</span>
                ${savePercent > 0 ? html`<span class="skio-v2-save-badge">SAVE ${savePercent}%</span>` : ''}
              </div>
            </div>

            ${this.getPricePerDayText(plan)
              ? html`<div class="skio-v2-price-per-day">${this.getPricePerDayText(plan)}</div>`
              : ''}

            ${benefitsAndGiftsBlock}
          </label>
        </div>
      </div>
    `
  },

  renderV2Onetime() {
    if (this.product.requires_selling_plan) return null

    const isSelected = !this.selectedSellingPlanGroup

    return html`
      <div
        class="group-container skio-v2-onetime ${isSelected ? 'group-container--selected' : ''}"
        @click=${() => this.selectSellingPlanGroup(null)}
      >
        <input
          id="one-time-${this.key}"
          class="group-input"
          name="skio-group-${this.key}"
          type="radio"
          value="One time purchase"
          ?checked=${isSelected}
          @change=${() => this.selectSellingPlanGroup(null)}
        />

        <label class="group-label" for="one-time-${this.key}">
          <div class="skio-v2-topline skio-v2-topline--onetime" style="${v2ToplineStyle}">
            ${this.options?.show_radio_selector ? this.radioTemplate() : ''}
            <span class="skio-v2-onetime-title">${this.options?.onetime_title || 'One-time purchase'}</span>
            <span class="skio-v2-onetime-price" style="margin-left:auto">${this.money(this.selectedVariant.price)}</span>
          </div>
        </label>
      </div>
    `
  },

  renderV2DesignPreview() {
    this.ensureV2StylesInjected()
    const opts = this.options || {}
    const variantPrice = this.selectedVariant?.price

    return html`
      <fieldset class="skio-plan-picker skio-plan-picker--v2" role="radiogroup" aria-label="Subscription preview">
        <div class="skio-v2-subscription-wrap">
          ${opts.show_top_banner
            ? html`<div class="skio-v2-banner">${opts.banner_text || 'MOST POPULAR & BEST VALUE'}</div>`
            : ''}
          <div class="group-container group-container--selected">
            <label class="group-label">
              <div class="skio-v2-topline" style="${v2ToplineStyle}">
                ${opts.show_radio_selector !== false ? this.radioTemplate() : ''}
                <span class="skio-v2-title">${opts.subscription_title || 'Subscription'}</span>
                <div class="skio-v2-prices" style="${v2PricesStyle}">
                  ${variantPrice
                    ? html`<span class="skio-v2-sale">${this.money(variantPrice)}</span>`
                    : html`<span class="skio-v2-sale">—</span>`}
                  <span class="skio-v2-save-badge">SAVE 10%</span>
                </div>
              </div>
              ${opts.show_price_per_day
                ? html`<div class="skio-v2-price-per-day">${opts.price_per_day_prefix || 'Clearer skin for just'} — ${opts.price_per_day_suffix || 'a day'}</div>`
                : ''}
              ${this.renderV2Benefits() || this.renderV2Gifts()
                ? html`
                    <div class="skio-v2-divider"></div>
                    ${this.renderV2Benefits()}
                    ${this.renderV2Gifts()}
                  `
                : ''}
            </label>
          </div>
        </div>
        <div class="group-container skio-v2-onetime">
          <label class="group-label">
            <div class="skio-v2-topline skio-v2-topline--onetime" style="${v2ToplineStyle}">
              ${opts.show_radio_selector !== false ? this.radioTemplate() : ''}
              <span class="skio-v2-onetime-title">${opts.onetime_title || 'One-time purchase'}</span>
              <span class="skio-v2-onetime-price" style="margin-left:auto">${variantPrice ? this.money(variantPrice) : '—'}</span>
            </div>
          </label>
        </div>
      </fieldset>
    `
  },

  renderV2() {
    this.applyV2Settings?.()
    this.ensureV2StylesInjected()

    const designMode = this.isDesignMode()
    const hasSellingPlans = Array.isArray(this.availableSellingPlanGroups) && this.availableSellingPlanGroups.length > 0
    const hasAllocations = Boolean(this.selectedVariant?.selling_plan_allocations?.length)

    if (designMode && (!this.viable() || !hasAllocations || !hasSellingPlans)) {
      return this.renderV2DesignPreview()
    }

    if (!this.viable() || this.options?.layout === 'hidden') {
      return designMode ? this.renderV2DesignPreview() : ''
    }

    if (!hasAllocations && !this.options?.show_without_subscription) {
      return designMode ? this.renderV2DesignPreview() : ''
    }

    if (!hasSellingPlans && !this.options?.show_without_subscription) {
      return designMode ? this.renderV2DesignPreview() : ''
    }

    const subscriptionCards = this.availableSellingPlanGroups?.length
      ? this.availableSellingPlanGroups.map((group, index) => this.renderV2SubscriptionCard(group, index))
      : []

    const onetimeFirst = this.options?.onetime_first

    return html`
      ${this.debug ? this.setupMode() : null}
      <fieldset class="skio-plan-picker skio-plan-picker--v2" role="radiogroup" aria-labelledby="skio-plan-picker-legend">
        <legend id="skio-plan-picker-legend" class="sr-only">${unsafeHTML(this.options?.legend_content || 'Select purchase option')}</legend>
        ${onetimeFirst ? this.renderV2Onetime() : ''}
        ${subscriptionCards}
        ${!onetimeFirst ? this.renderV2Onetime() : ''}
      </fieldset>
    `
  },
})

window.__skioV2ExtensionLoaded = true

document.querySelectorAll('skio-plan-picker[ui-version="v2"]').forEach(picker => {
  picker.applyTargetFormId?.()
  picker.applyV2Settings?.()
  picker.ensureV2StylesInjected?.()

  if (typeof picker.bindFormEvents === 'function') {
    picker.bindFormEvents()
  }

  picker.requestUpdate()
})

document.addEventListener('variant:change', event => {
  if (!event.detail?.variant) return

  document.querySelectorAll('skio-plan-picker[ui-version="v2"]').forEach(picker => {
    picker.syncVariantFromEvent?.(event.detail.variant)
    picker.requestUpdate()
  })
})

export {}
